import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import GuestInvitePopupView from "views/guest/GuestInvitePopupView";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import PersistentModels from "services/PersistentModels";
import Utils from "models/helper/Utils";
import { shouldShowUpgradeCTA, getRecommendedUpgradeMode } from "models/helper/WorkspaceUpgradeCTA";
import ti18n from "i18n!nls/GuestFeaturei18n";

// Mirror ProjectSharePopupController._isProRequiredError. Backend throws 403
// for both the older "Pro subscription is required" personal guest gate and
// the newer "paid plan is required" workspace materialization gate. Require
// BOTH status 403 AND an invite-related message substring before routing to
// the upgrade dialog — see ProjectSharePopupController.js for the full
// rationale. TODO: replace with a structured errorCode='guest/pro_required'
// check once the backend tags this rejection.
function _isProRequiredError(error) {
    if (!error) return false;
    var message = (error.message || error.errorMessage || '').toString();
    var normalizedMessage = message.toLowerCase();
    var status = error.statusCode || error.status || error.httpStatusCode;
    var statusOk = status === 403 || status === '403';
    var messageOk = message.indexOf('Pro subscription is required') !== -1
        || (normalizedMessage.indexOf('paid plan is required') !== -1
            && (normalizedMessage.indexOf('invite') !== -1
                || normalizedMessage.indexOf('reviewer') !== -1
                || normalizedMessage.indexOf('member') !== -1));
    return statusOk && messageOk;
}

const GuestInvitePopupController = Chaplin.Controller.extend({

    initialize: function(options) {
        _.extend(this, Backbone.Events);
        this._parentController = options.parentController;
        this._guestRecordId = null;
    },

    getModel: function() {
        return this.model;
    },

    getView: function() {
        return this.view;
    },

    onPopupClosed: function() {
        // Cleanup if needed
    },

    handleInvite: async function(email, accessLevel) {
        try {
            await TrebbleClientAPIHelper.getInstance().inviteGuestToProject({
                guestEmail: email,
                accessLevel: accessLevel
            });
            window.alertSuccessMessage(window.getI18n(ti18n, "INVITE_SUCCESS"));
        } catch (error) {
            console.error('Guest invite error:', error);
            // Mirror ProjectSharePopupController.handleInvite: when the
            // backend rejects the invite because a paid plan is required,
            // surface a dedicated upgrade-CTA dialog instead
            // of the generic "Could not send invite" alert.
            //
            // Gate on shouldShowUpgradeCTA so users who can't actually
            // upgrade (e.g. already own a paid workspace, hit a per-seat
            // limit) fall back to the generic alert rather than seeing
            // a dialog whose Upgrade button no-ops.
            if (_isProRequiredError(error) && shouldShowUpgradeCTA()) {
                error.handledByUpgradePrompt = true;
                this._promptUpgradeFromInviteBlocked();
                throw error;
            }
            window.alertErrorMessage(window.getI18n(ti18n, "INVITE_ERROR"));
            throw error;
        }
    },

    _promptUpgradeFromInviteBlocked: function() {
        var title = window.getI18n(ti18n, "PRO_REQUIRED_TO_INVITE_TITLE")
            || "A Pro subscription is required to invite guests";
        var message = window.getI18n(ti18n, "PRO_REQUIRED_TO_INVITE_MESSAGE")
            || "Upgrade your plan to invite guests to your projects.";
        var cancelLabel = window.getI18n(ti18n, "CANCEL") || "Cancel";
        var upgradeLabel = window.getI18n(ti18n, "UPGRADE") || "Upgrade";

        // Caller (handleInvite) already gated on shouldShowUpgradeCTA
        // before opening this dialog, so we don't re-check on confirm.
        // See ProjectSharePopupController._promptUpgradeFromInviteBlocked
        // for the full rationale on the gate-upfront pattern.
        Utils.getInstance().showUserConfirmationDialog(
            title,
            message,
            null,
            { buttonLabels: [cancelLabel, upgradeLabel] }
        ).then(function(confirmed) {
            if (!confirmed) return;
            var mode = getRecommendedUpgradeMode();
            PersistentModels.getInstance().getRouter().showUpgradeToTeamPopup(
                this._parentController || this,
                { mode: mode }
            );
        }.bind(this));
    },

    handleRevoke: async function() {
        try {
            await TrebbleClientAPIHelper.getInstance().revokeGuestAccess({
                guestRecordId: this._guestRecordId
            });
            window.alertSuccessMessage(window.getI18n(ti18n, "REVOKE_SUCCESS"));
        } catch (error) {
            console.error('Guest revoke error:', error);
            window.alertErrorMessage(window.getI18n(ti18n, "REVOKE_ERROR"));
            throw error;
        }
    },

    _canSwapGuest: function(guestInfo) {
        if (!guestInfo) {
            return true;
        }
        var lastGuestSwapDate = guestInfo.lastGuestSwapDate || guestInfo.lastSwapDate;
        if (!lastGuestSwapDate) {
            return true;
        }
        var lastSwapDate = new Date(lastGuestSwapDate);
        var now = new Date();
        var canSwap = lastSwapDate.getFullYear() !== now.getFullYear() || lastSwapDate.getMonth() !== now.getMonth();
        return canSwap;
    },

    buildModelAndRenderView: function() {
        return TrebbleClientAPIHelper.getInstance().getMyGuestInfo().then((function(guestInfo) {
            const hasGuest = guestInfo && guestInfo.email;
            const mode = hasGuest ? 'manage' : 'invite';
            const canSwap = this._canSwapGuest(guestInfo);

            // Store guestRecordId for revoke operations
            if (guestInfo && guestInfo.guestRecordId) {
                this._guestRecordId = guestInfo.guestRecordId;
            }

            const params = {
                mode: mode,
                currentGuestEmail: hasGuest ? guestInfo.email : undefined,
                currentGuestName: hasGuest ? guestInfo.name : undefined,
                accessLevel: hasGuest ? (guestInfo.accessLevel || 'can_edit') : undefined,
                canSwap: canSwap,
                onInvite: this.handleInvite.bind(this),
                onRevoke: this.handleRevoke.bind(this),
                i18nModule: ti18n
            };

            this.view = new GuestInvitePopupView(params);
            this.listenTo(this.view, "closed", this.onPopupClosed);

            this.view.render();
        }).bind(this)).catch((function(error) {
            // If loading guest info fails, default to invite mode
            console.error('Failed to load guest info:', error);

            const params = {
                mode: 'invite',
                canSwap: true,
                onInvite: this.handleInvite.bind(this),
                onRevoke: this.handleRevoke.bind(this),
                i18nModule: ti18n
            };

            this.view = new GuestInvitePopupView(params);
            this.listenTo(this.view, "closed", this.onPopupClosed);

            this.view.render();
        }).bind(this));
    }
});

export default GuestInvitePopupController;
